import React, { useState, useEffect, useCallback } from 'react';
import {
  Typography,
  Paper,
  Box,
  Button,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Snackbar,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Groups as GroupsIcon,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import AdminLayout from '../../../layouts/AdminLayout';
import unifiedApi from '../../../utils/unifiedApi';
import { useCanManageLeague } from '../../../contexts/PermissionsContext';
import TeamDetailsEdit from '../../../components/team/TeamDetailsEdit';
import TeamRosterManage from '../../../components/team/TeamRosterManage';

const TeamManagementPage = () => {
  const { leagueId } = useParams();
  const navigate = useNavigate();
  const canManageLeague = useCanManageLeague(parseInt(leagueId));

  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // TeamDetailsEdit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editDialogMode, setEditDialogMode] = useState('edit');
  const [editingTeamId, setEditingTeamId] = useState(null);
  const [rosterTeam, setRosterTeam] = useState(null);

  // Delete confirmation state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingTeam, setDeletingTeam] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });

  const fetchTeams = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await unifiedApi.getLeagueTeamsWithManagers(leagueId);
      setTeams(data || []);
    } catch {
      setError('Failed to load teams.');
    } finally {
      setLoading(false);
    }
  }, [leagueId]);

  useEffect(() => {
    fetchTeams();
  }, [fetchTeams]);

  // Create / Edit dialog handlers
  const handleOpenCreate = () => {
    setEditDialogMode('create');
    setEditingTeamId(null);
    setEditDialogOpen(true);
  };

  const handleOpenEdit = (team) => {
    setEditDialogMode('edit');
    setEditingTeamId(team.id);
    setEditDialogOpen(true);
  };

  const handleDialogClose = () => {
    setEditDialogOpen(false);
  };

  const handleDialogSave = () => {
    setEditDialogOpen(false);
    setSnackbar({ open: true, message: editDialogMode === 'create' ? 'Team created successfully.' : 'Team updated successfully.' });
    fetchTeams();
  };

  const handleRosterUpdated = () => {
    setSnackbar({ open: true, message: 'Team roster updated successfully.' });
    fetchTeams();
  };

  // Delete handlers
  const handleOpenDelete = (team) => {
    setDeletingTeam(team);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingTeam) return;
    setDeleting(true);
    try {
      await unifiedApi.deleteTeam(deletingTeam.id);
      setSnackbar({ open: true, message: `"${deletingTeam.name}" deleted.` });
      setDeleteDialogOpen(false);
      setDeletingTeam(null);
      fetchTeams();
    } catch {
      setSnackbar({ open: true, message: 'Failed to delete team.' });
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setDeletingTeam(null);
  };

  if (loading) {
    return (
      <AdminLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress size={60} />
        </Box>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          sx={{ mr: 2 }}
        >
          Back
        </Button>
        <Typography variant="h5">Team Management</Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {canManageLeague && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreate}>
            Add Team
          </Button>
        </Box>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Team Name</TableCell>
              <TableCell>Players</TableCell>
              <TableCell>Manager</TableCell>
              <TableCell>Jersey Color</TableCell>
              {canManageLeague && <TableCell align="right">Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {teams.length === 0 && !error ? (
              <TableRow>
                <TableCell colSpan={canManageLeague ? 5 : 4} align="center">
                  No teams found for this league.
                </TableCell>
              </TableRow>
            ) : (
              teams.map((team) => (
                <TableRow key={team.id}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {team.jerseyColorHex && (
                        <Box
                          sx={{
                            width: 16,
                            height: 16,
                            borderRadius: '50%',
                            backgroundColor: team.jerseyColorHex,
                            border: '1px solid',
                            borderColor: 'divider',
                            flexShrink: 0,
                          }}
                        />
                      )}
                      {team.name}
                    </Box>
                  </TableCell>
                  <TableCell>{team.playerCount}</TableCell>
                  <TableCell>{team.manager?.fullName || '—'}</TableCell>
                  <TableCell>{team.jerseyColorName || '—'}</TableCell>
                  {canManageLeague && (
                    <TableCell align="right">
                      <Tooltip title="Manage roster">
                        <IconButton size="small" onClick={() => setRosterTeam(team)} aria-label={`Manage roster ${team.name}`}>
                          <GroupsIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => handleOpenEdit(team)} aria-label={`Edit ${team.name}`}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" color="error" onClick={() => handleOpenDelete(team)} aria-label={`Delete ${team.name}`}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create / Edit Dialog — reuses TeamDetailsEdit from public team page */}
      <TeamDetailsEdit
        open={editDialogOpen}
        mode={editDialogMode}
        teamId={editingTeamId}
        leagueId={parseInt(leagueId)}
        onClose={handleDialogClose}
        onSave={handleDialogSave}
      />

      <TeamRosterManage
        open={Boolean(rosterTeam)}
        onClose={() => setRosterTeam(null)}
        teamId={rosterTeam?.id}
        teamName={rosterTeam?.name}
        onUpdated={handleRosterUpdated}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Delete Team</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>{deletingTeam?.name}</strong>? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={deleting}>
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained" disabled={deleting}>
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ open: false, message: '' })}
        message={snackbar.message}
      />
    </AdminLayout>
  );
};

export default TeamManagementPage;
